import React, { useState, useEffect, useCallback, useRef } from 'react'

// Deps
import { connect } from 'react-redux';
import { redirect } from 'controllers/navigator'
import differenceInDays from 'date-fns/differenceInDays';
import parseISO from 'date-fns/parseISO';

// Partials
import Btn from 'views/shared/partials/btn'
import Link from 'views/shared/partials/link'
import Loader from 'views/shared/partials/loader'
import Pricebox from 'views/shared/partials/pricebox'
import ConversionInfo from 'views/shared/partials/conversion-info'
import OrderStatus from 'views/shared/partials/order-status'
import CommerceOrderStatus from 'views/shared/partials/commerce-order-status'
import Tabs from 'views/shared/partials/tabs'
import Image from 'views/shared/partials/image'
import { Table, TR, TD } from 'views/shared/partials/table'

// Utils
import { openModal } from 'utils/modals'

// Functions
import formatDate from 'functions/format-date'
import formatNumber from 'functions/format-number'
import getCurrencySymbol from 'functions/get-currency-symbol'
import getCurrency from 'functions/get-currency'
import parseHtml from 'functions/parse-html'
import parseProducts from 'functions/parse-products'

// Sections
import Breadcrumbs from 'views/admin/sections/breadcrumbs'

// Services
import orderServices from 'services/order'

// Data
import notificationTypes from 'data/notification-types'
import orderStatusIndex from 'data/order-status-index'
import commerceServices from "../../../services/commerce";

const mapStateToProps = (state, ownProps) => {
	return {
		userData: state.user.data,
	};
};

const OrderDetail = ({userData, ...props}) => {
	const orderID = props.match.params.id;

	const defaultSuccessfulPaymentQuery = useRef({
		page: 1,
		sort_by: 'created_at',
		status_code: 1,
		order_business_code: orderID,
	})

	const defaultFailedPaymentQuery = useRef({
		page: 1,
		sort_by: 'created_at',
		status_code: 0,
		order_business_code: orderID,
	})

	const defaultRefundQuery = useRef({
		page: 1,
		sort_by: 'created_at',
		status_code: 2,
		order_business_code: orderID,
	})

	const [data, setData] = useState(false);

	const [successfulPaymentQuery, setSuccessfulPaymentQuery] = useState(defaultSuccessfulPaymentQuery.current)
	const [failedPaymentQuery, setFailedPaymentQuery] = useState(defaultFailedPaymentQuery.current)
	const [refundQuery, setRefundQuery] = useState(defaultRefundQuery.current)

	const [successfulPayments, setSuccessfulPayments] = useState(false);
	const [failedPayments, setFailedPayments] = useState(false);
	const [refunds, setRefunds] = useState(false);
	const [activeTab, setActiveTab] = useState('info');
	const [deletingNote, setDeletingNote] = useState(false);

	const [loadingPayment, setLoadingPayment] = useState(false)

	const getData = useCallback((force) => {
		if(!data || data.business_order_code !== orderID || force === true) {
			setData(false);
			orderServices.getOrder(orderID).then((payload) => {

				setData({
					...payload,
					due_days: differenceInDays(new Date(), parseISO(payload.due_date)),
					parsedProducts: parseProducts(payload.products),
				});
			});

			setSuccessfulPaymentQuery(defaultSuccessfulPaymentQuery.current);
			setFailedPaymentQuery(defaultFailedPaymentQuery.current);
			setRefundQuery(defaultRefundQuery.current);
		}

		// getInstallmentOptions(orderID);
	}, [orderID, data]);

	const deleteOrder = () => {
		openModal(
			'confirm',
			{
				message: 'Ödeme talebini iptal etmek istediğinizden emin misiniz?',
				onConfirm: () => {
					orderServices.deleteOrder(data.business_order_code).then(() => {
						redirect('orders');
					}).catch(() => {})
				}
			}
		)
	}

	const deleteOrderNote = (noteID) => {
		openModal(
			'confirm',
			{
				message: 'Notu silmek istediğinizden emin misiniz?',
				onConfirm: () => {
					setDeletingNote(noteID);

					orderServices.deleteOrderNote(orderID, noteID).then(() => {
						getData(true);
					}).catch((e) => {
						console.warn('Order note deletion error: ', e);
					}).finally(() => {
						setDeletingNote(false);
					})
				}
			}
		)
	}

	const paginatePayments = (type, page) => {
		if(type === 'successful') {
			setSuccessfulPaymentQuery({...defaultSuccessfulPaymentQuery.current, page: page})
		}
		else if(type === 'failed') {
			setFailedPaymentQuery({...defaultFailedPaymentQuery.current, page: page})
		}
		else if(type === 'refund') {
			setRefundQuery({...defaultRefundQuery.current, page: page})
		}
	}

	const getSubPayments = (type, query) => {
		if (type === 'successful') {
			setSuccessfulPayments(false);
			orderServices.listPayments(query).then((payload) => {
				setSuccessfulPayments(payload);
			}).catch(() => {
				setSuccessfulPayments(null);
			})
		}
		else if (type === 'failed') {
			setFailedPayments(false);
			orderServices.listPayments(query).then((payload) => {
				setFailedPayments(payload);
			}).catch(() => {
				setFailedPayments(null);
			})
		}
		else if (type === 'refund') {
			setRefunds(false);
			orderServices.listPaymentRefunds(query).then((payload) => {
				setRefunds(payload);
			}).catch(() => {
				setRefunds(null);
			})
		}
	}

	useEffect(() => {
		getSubPayments('successful', successfulPaymentQuery);
	}, [successfulPaymentQuery])

	useEffect(() => {
		getSubPayments('failed', failedPaymentQuery);
	}, [failedPaymentQuery])

	useEffect(() => {
		getSubPayments('refund', refundQuery);
	}, [refundQuery])

	useEffect(() => {
		getData();
	}, [orderID, getData])

	const addressDelivery = (data && data.product_delivery_method_id !== null) ? data.product_delivery_method_id === 2 : null;

	return (
		<main className="page order-detail">
			<Breadcrumbs
				midSections={[
					{href: 'orders'}
				]} />
			<section className="portlet section detail-info loader-container">
				<Loader loading={!data} />
				{data &&
					<React.Fragment>
						<div className="portlet-header">
							<h1 className="portlet-title">
								<span>{(data.client.invoice_type === 2 && data.client.company_name) ? data.client.company_name : (data.client.executive_name + ' ' + data.client.executive_surname)}</span>
								{(data.client.invoice_type === 2 && data.client.company_name) &&
									<span className="title-subtitle">{data.client.executive_name} {data.client.executive_surname}</span>
								}
								{(data.products && data.products.length > 0) &&
									<span className="label category-commerce">Ürün Satış</span>
								}
							</h1>

							<div className="header-controls">
								<Btn
									white small wide
									className="controls-edit"
									disabled={data.order_plan_id || data.is_product_order}
									onClick={() => { openModal('update-order', { orderCode: data.business_order_code, onSuccess: () => { getData(true); }}); }}
									icon="edit">
									Bilgileri Düzenle
								</Btn>
								<Btn
									small wide error outline
									className="controls-delete"
									onClick={deleteOrder}
									icon="trash">
									İptal Et
								</Btn>
								{data.is_product_order &&
									<Btn
										green small wide
										tag="link"
										href="commerceOrderDetail"
										params={{ id: data.business_order_code }}
										icon="products">
										Sipariş Detayı
									</Btn>
								}
								{data.status !== 5 &&
									<Btn
										small wide success outline
										className="controls-cancel"
										onClick={() => { openModal('cancel-order', { orderData: data, onSuccess: () => { getData(true); }}); }}
										icon="check">
										Kapat
									</Btn>
								}
								<Btn
									blue small wide
									className="controls-sendlink"
									onClick={() => {
										openModal('order-summary', { orderCode: data.business_order_code, onNotificationSent: () => { getData(true); } })
									}}
									icon="link">
									Link Gönder
								</Btn>
							</div>
						</div>

						<div className="portlet-section">
							<div className="portlet-infobar">
								<div className="grid-container">
									<div className="row">
										<div className="col x3">
											<div className="infobar-section">
												<strong className="section-title">Ödeme Tutarı</strong>
												<span className="section-data">
													<Pricebox
														showCode big
														price={data.total_amount}
														currency={data.price_type.currency_symbol} />
												</span>
											</div>
										</div>
										<div className="col x3">
											<div className="infobar-section">
												<strong className="section-title">Kalan Bakiye</strong>
												<span className="section-data">
													<p>
														<Pricebox
															showCode big
															price={data.remaining_amount}
															currency={data.price_type.currency_symbol} />
													</p>
													{(data.remaining_amount && data.remaining_amount > 0 && data.currency_exchange_type_id !== "0" && data.currency_exchange_rate.rate) &&
														<p>
															<span className="label dark text-regular">
																<Pricebox
																	monochrome
																	price={(data.remaining_amount * data.currency_exchange_rate.rate)} />
															</span>
														</p>
													}
													{data.due_days > 0 &&
														<p>
															<span className="label error">{data.due_days} Gün Gecikti</span>
														</p>
													}
												</span>
											</div>
										</div>
										<div className="col x3">
											<div className="infobar-section">
												<strong className="section-title">Ödenen Bakiye</strong>
												<span className="section-data">
													<p>
														<Pricebox
															big
															price={data.payment_amount}
															currency={data.price_type.currency_symbol} />
													</p>
													{(data.currency_exchange_type_id > 0 && data.currency_exchange_amount && data.currency_exchange_amount !== "0") &&
														<React.Fragment>
															<p className="price-conversion">
																<span className="label dark text-regular">
																	<Pricebox
																		monochrome
																		price={data.currency_exchange_amount} />
																</span>
																<Pricebox
																	monochrome
																	currency={data.price_type.currency_symbol}
																	price={data.currency_amount} />
															</p>
															<p>
																<span className="label dark text-regular">
																	<Pricebox
																		monochrome
																		currency={data.price_type.currency_symbol}
																		price={data.payment_amount - data.currency_amount} />
																</span>
															</p>
														</React.Fragment>
													}
												</span>
											</div>
										</div>
										<div className="col x3">
											<div className="infobar-section">
												<strong className="section-title">İade Edilen Bakiye</strong>
												<span className="section-data">
													<p>
														<Pricebox
															big
															price={data.refund_amount}
															currency={data.price_type.currency_symbol} />
													</p>
													{(data.refund_amount > 0 && data.refund_amount !== "0" && data.currency_exchange_type_id > 0 && data.currency_exchange_rate.rate) &&
														<React.Fragment>
															<p className="price-conversion">
																<span className="label dark text-regular">
																	<Pricebox
																		monochrome
																		price={data.currency_exchange_refund_amount} />
																</span>
																<Pricebox
																	monochrome
																	currency={data.price_type.currency_symbol}
																	price={data.currency_refund_amount} />
															</p>
															<p>
																<span className="label dark text-regular">
																	<Pricebox
																		monochrome
																		currency={data.price_type.currency_symbol}
																		price={data.refund_amount - data.currency_refund_amount} />
																</span>
															</p>
														</React.Fragment>
													}
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						{data.is_product_order &&
							<div className="portlet-section bottom border-top">
								<div className="grid-container">
									<div className="row">
										<div className="col">
											<div className="alert warning">
												<i className="alert-icon icon-products big"></i>
												<div className="alert-content">
													Bu ödeme bir ürün siparişi olarak oluşturulmuş. Detaylı bilgi için sipariş detayını inceleyebilirsiniz.
												</div>
												<div className="alert-controls">
													<Btn
														blue small wide
														tag="link"
														href="commerceOrderDetail"
														params={{ id: data.business_order_code }}>
														Sipariş Detayına Git
													</Btn>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						}
						{(data.currency_exchange_type_id > 0 || data.company_payment_type_id !== null || data.order_plan_id !== null) &&
							<div className="portlet-section bottom border-top">
								<div className="portlet-infobar horizontal">
									<div className="grid-container">
										{data.currency_exchange_type_id > 0 &&
											<div className="row">
												<div className="col">
													<div className="alert">
														<i className="alert-icon icon-conversion big yellow-dark"></i>
														<div className="alert-content">
															Ödeme anında döviz kuru ile çevrilir ve Türk lirası olarak ödenir. <ConversionInfo
																exchangeType={data.currency_exchange_rate.id}
																currency={getCurrency(data.currency_exchange_rate.currency).symbol}
																price={data.currency_exchange_rate.rate} />
														</div>
													</div>
												</div>
											</div>
										}
										{data.company_payment_type_id &&
											<div className="row">
												<div className="col">
													<div className="alert">
														<i className="alert-icon icon-payment yellow-dark"></i>
														<div className="alert-content">
															Bu işlem sadece <span className="label">{data.company_payment_type.payment_type.name}</span> ile ödenecek şekilde sabitlenmiştir.
															{data.company_payment_installments && data.company_payment_installments.length > 0 &&
																<span>
																	({data.company_payment_installments.map((installment, nth) => (<span key={nth}>{nth > 0 ? ', ' : ''}{installment.installment_text}</span>))})
																</span>
															}
														</div>
													</div>
												</div>
											</div>
										}
										{data.order_plan_id &&
											<div className="row">
												<div className="col">
													<div className="alert">
														<i className="alert-icon icon-time yellow-dark"></i>
														<div className="alert-content">
															Bu talep, <Link className="link" href="orderPlanDetail" params={{id: data.order_plan_id}}>ödeme planı</Link> üzerinden otomatik olarak oluşturulmuştur.
														</div>
													</div>
												</div>
											</div>
										}
									</div>
								</div>
							</div>
						}
						<Tabs
							onChange={(tab) => {
								setActiveTab(tab);
							}}>
							<div
								index="info"
								active={activeTab === 'info'}
								label="Ödeme Bilgileri">
								<div className="portlet-infobar horizontal">
									<div className="grid-container">
										{/*<strong className="infobar-title">Ödeme Bilgileri</strong>*/}
										<div className="row">
											<div className="col x6">
												<div className="infobar-section">
													<strong className="section-title">Firma Sipariş Kodu #</strong>
													<span className="section-data">
														{data.business_order_code}
													</span>
												</div>

												<div className="infobar-section">
													<strong className="section-title">Sipariş Kodu</strong>
													<span className="section-data">
														{data.order_code}
													</span>
												</div>

												<div className="infobar-section">
													<strong className="section-title">İşletme Grubu</strong>
													<span className="section-data">
														{data.group.name}
													</span>
												</div>

												<div className="infobar-section">
													<strong className="section-title">Ödeme Oluşturan</strong>
													<span className="section-data">
														{data.account.name} {data.account.surname}
													</span>
												</div>
											</div>
											<div className="col x6">
												<div className="infobar-section">
													<strong className="section-title">Sipariş Tutarı</strong>
													<span className="section-data">
														<Pricebox
															showCode monochrome
															price={data.total_amount}
															currency={data.price_type.currency_symbol} />
													</span>
												</div>
												<div className="infobar-section">
													<strong className="section-title">Sipariş Durumu</strong>
													<span className="section-data">
														<OrderStatus label status={data.status} />
													</span>
												</div>
												{data.is_product_order &&
													<div className="infobar-section">
														<strong className="section-title">Ürün Sipariş Durumu</strong>
														<span className="section-data">
															<CommerceOrderStatus label status={data.product_order_status} />
														</span>
													</div>
												}
												<div className="infobar-section">
													<strong className="section-title">Oluşturulma Tarihi</strong>
													<span className="section-data">
														{formatDate(data.created_at)}
													</span>
												</div>
												{!data.is_product_order &&
													<div className="infobar-section">
														<strong className="section-title">Ödeme Vadesi</strong>
														<span className="section-data">
															{formatDate(data.due_date)}
														</span>
													</div>
												}
											</div>
										</div>

										<strong className="infobar-title">Ödeme Açıklaması</strong>
										<div className="row">
											<div className="col">
												<div className="infobar-section">
													<div className="section-data full">
														{data.description ? parseHtml(data.description) : '-'}
													</div>
												</div>
											</div>
										</div>

										<strong className="infobar-title">Müşteri Bilgileri</strong>
										<div className="row">
											<div className="col x6">
												<div className="infobar-section">
													<strong className="section-title">Müşteri Tipi</strong>
													<span className="section-data">
														{data.client.invoice_type === 1 ?
															<label className="label dark">
																Bireysel
															</label>
															:
															<label className="label">
																Kurumsal
															</label>
														}
													</span>
												</div>
												<div className="infobar-section">
													<strong className="section-title">{data.client.invoice_type === 2 ? 'Yetkili' : 'Müşteri'} Adı Soyadı</strong>
													<span className="section-data">
														{data.client.executive_name} {data.client.executive_surname}
													</span>
												</div>
												<div className="infobar-section">
													<strong className="section-title">Müşteri Kodu #:</strong>
													<span className="section-data">
														{data.client.client_code}
													</span>
												</div>
												<div className="infobar-section">
													<strong className="section-title">Cep Telefonu</strong>
													<span className="section-data">
														{data.client.phone_number}
													</span>
												</div>
												<div className="infobar-section">
													<strong className="section-title">E-Posta Adresi</strong>
													<span className="section-data">
														{data.client.email}
													</span>
												</div>
											</div>
											<div className="col x6">
												<div className="infobar-section">
													<strong className="section-title">Firma Kısa Adı</strong>
													<span className="section-data">
														<Link href="clientDetail" className="link" params={{id: data.client.client_code}}>{data.client.company_name ? data.client.company_name : '-'}</Link>
													</span>
												</div>
												{data.client.invoice_type === 2 ?
													<React.Fragment>
														<div className="infobar-section">
															<strong className="section-title">Vergi Numarası</strong>
															<span className="section-data">
																{data.client.tax_number ? data.client.tax_number : '-'}
															</span>
														</div>
														<div className="infobar-section">
															<strong className="section-title">Vergi Dairesi</strong>
															<span className="section-data">
																{data.client.tax_office ? data.client.tax_office : '-'}
															</span>
														</div>
														<div className="infobar-section">
															<strong className="section-title">Vergi Ünvanı</strong>
															<span className="section-data">
																{data.client.invoice_name ? data.client.invoice_name : '-'}
															</span>
														</div>
													</React.Fragment>:
													<React.Fragment>
														<div className="infobar-section">
															<strong className="section-title">TC Kimlik No</strong>
															<span className="section-data">
																{data.client.identity_number ? data.client.identity_number : '-'}
															</span>
														</div>
													</React.Fragment>
												}

												<div className="infobar-section">
													<strong className="section-title">Fatura Adresi</strong>
													<span className="section-data">
														{data.client.invoice_address ? data.client.invoice_address : '-'}
													</span>
												</div>

												<div className="infobar-section">
													<strong className="section-title">Serbest Ödeme Özelliği</strong>
													<span className="section-data">
														{data.client.custom_order ? 'Aktif' : 'Deaktif'}
													</span>
												</div>
											</div>
										</div>

										{(data.is_product_order && addressDelivery !== null) &&
											<>
												<strong className="infobar-title">Sipariş Bilgileri</strong>
												<div className="row">
													<div className="col x6">
														<div className="infobar-section">
															<strong className="section-title">Teslimat Yöntemi</strong>
															<span className="section-data">
																{addressDelivery ? 'Adrese Teslimat' : <>Mağazada Teslimat{data.target_group && <>: <br /><strong>{data.target_group.name}</strong></>}</>}
															</span>
														</div>
													</div>
												</div>
												{addressDelivery &&
													<>
														<strong className="infobar-title">Teslimat Bilgileri</strong>
														<div className="row">
															<div className="col x6">
																<div className="infobar-section">
																	<strong className="section-title">Adres İsmi</strong>
																	<span className="section-data">
																		{data.client.delivery_title}
																	</span>
																</div>
																<div className="infobar-section">
																	<strong className="section-title">Telefon Numarası</strong>
																	<span className="section-data">
																		{data.client.delivery_phone}
																	</span>
																</div>
																<div className="infobar-section">
																	<strong className="section-title">Telefon Numarası</strong>
																	<span className="section-data">
																		{data.client.delivery_owner}
																	</span>
																</div>
															</div>
															<div className="col x6">
																<div className="infobar-section">
																	<strong className="section-title">İl</strong>
																	<span className="section-data">
																		{data.client.delivery_city}
																	</span>
																</div>
																<div className="infobar-section">
																	<strong className="section-title">İlçe</strong>
																	<span className="section-data">
																		{data.client.delivery_district}
																	</span>
																</div>
																<div className="infobar-section">
																	<strong className="section-title">Adres</strong>
																	<span className="section-data">
																		{data.client.delivery_address}
																	</span>
																</div>
															</div>
														</div>
													</>
												}
											</>
										}
									</div>
								</div>
							</div>
							<div
								index="actions"
								active={activeTab === 'actions'}
								badge={data.status_history.length}
								label="İşlem Geçmişi">
								<Table emptyMessage="İşlem geçmişi yok">
									{data.status_history.map((log, nth) => (
										<TR key={nth}>
											<TD>
												<span className={'label big ' + orderStatusIndex[log.status.toString()].color}>{log.status_name}</span>
											</TD>
											<TD>
												<p>
													{formatDate(log.created_at, 'dd MMMM yyyy')}
												</p>
												<p className="table-subtitle">
													{formatDate(log.created_at, 'HH:mm')}
												</p>
											</TD>
										</TR>
									))}
								</Table>
							</div>
							<div
								index="notifications"
								active={activeTab === 'notifications'}
								badge={data.notification_history.length}
								label="Bildirim Geçmişi">
								<Table emptyMessage="Bildirim geçmişi yok">
									{data.notification_history.map((log, nth) => (
										<TR key={nth}>
											<TD>
												<p>
													<strong>{notificationTypes[log.method]}</strong> ({log.type === 'sms' ? 'SMS' : 'E-Posta'})
												</p>
												<p>{log.contact}</p>
											</TD>
											<TD>
												<p>
													{formatDate(log.created_at, 'dd MMMM yyyy')}
												</p>
												<p className="table-subtitle">
													{formatDate(log.created_at, 'HH:mm')}
												</p>
											</TD>
										</TR>
									))}
								</Table>
							</div>
							<div
								index="notes"
								active={activeTab === 'notes'}
								badge={data.client_order_notes.length}
								label="Yönetici Notları">
								<div className="grid-container">
									<div className="row">
										<div className="col al-right">
											<Btn
												small wide blue
												onClick={() => {
													openModal('admin-note', { orderID: orderID, onSuccess: () => { getData(true) }});
												}}
												icon="plus">
												Not Ekle
											</Btn>
										</div>
									</div>
									<div className="row">
										<div className="col">
											<Table emptyMessage="Yönetici notu yok">
												{data.client_order_notes.map((note, nth) => (
													<TR key={nth}>
														<TD>
															{note.order_note}
														</TD>
														<TD className="al-right">
															<p>
																<strong>{note.account.name} {note.account.surname}</strong>
															</p>
															<p className="table-subtitle">
																{formatDate(note.created_at)}
															</p>
															{note.created_at !== note.updated_at &&
																<p className="table-subtitle">
																	(Güncelleme: {formatDate(note.updated_at)})
																</p>
															}
														</TD>
														{userData.is_owner &&
															<TD>
																<React.Fragment>
																	<Btn
																		disabled={deletingNote}
																		loading={deletingNote && deletingNote === note.id}
																		onClick={() => {
																			deleteOrderNote(note.id)
																		}}
																		small error icon="trash">
																		Sil
																	</Btn>
																</React.Fragment>
															</TD>
														}
													</TR>
												))}
											</Table>
										</div>
									</div>
								</div>
							</div>
						</Tabs>
					</React.Fragment>
				}
			</section>

			{(data && data.parsedProducts && (data.parsedProducts.active.length > 0 || data.parsedProducts.returned.length > 0)) &&
				<section className="portlet section detail-products">
					<div className="portlet-header">
						<h1 className="portlet-title">
							Ürünler
						</h1>
                        <div className="header-controls">
                            <Btn
                                white small wide
                                icon="refresh"
                                onClick={() => { openModal('return-order-product', {orderData: data, onSuccess: () => { getData(true); }}); }}>
                                İade İşlemleri
                            </Btn>
                        </div>
					</div>

					<div className="portlet-section">
						<Table
							emptyMessage="Hiç ürün bulunmuyor."
							className="products-table"
							heading={
								<React.Fragment>
									<th>Görsel</th>
									<th>Ürün</th>
									<th>Fiyat</th>
								</React.Fragment>
							}>
							{data.parsedProducts.active.map((product, nth) => (
								<TR key={nth} className="table-product">
									<TD className="product-image">
										<div className="table-imagewrap">
											<Image src={product.product.image_link} />
										</div>
									</TD>
									<TD className="product-description">
										<strong>
											{product.product.title}
										</strong>
										<p><strong>{product.quantity} adet</strong> x <Pricebox monochrome price={product.price_new} /></p>
										<p className="table-subtitle">
											{product.product.mpn}
										</p>
										{addressDelivery &&
											<p className="table-subtitle">
												{product.product.cargo_day} gün içinde kargoya verilir.
											</p>
										}
									</TD>
									<TD className="product-price">
										<Pricebox price={product.total_price} />
									</TD>
								</TR>
							))}
							{data.parsedProducts.returned.map((product, nth) => (
								<TR key={nth} className="table-product">
									<TD className="product-image">
										<div className="table-imagewrap">
											<Image src={product.product.image_link} />
										</div>
									</TD>
									<TD className="product-description">
										<strong>
											{product.product.title}
										</strong>
										<p><strong>{product.quantity} adet</strong></p>
										<p>
											<span className="label warning">İade Edildi</span>
										</p>
										<p className="table-subtitle">
											{product.product.product_id}
										</p>
									</TD>
									<TD className="product-price">
										<Pricebox price={product.price_new*product.quantity} />
									</TD>
								</TR>
							))}
						</Table>
					</div>
				</section>
			}

			<div className="portlet detail-payments">
				<div className="portlet-header">
					<h2 className="portlet-title">
						Ödemeler & Denemeler
					</h2>
				</div>
				<Tabs>
					{successfulPayments !== null &&
						<div
							index="successful"
							badge={successfulPayments ? successfulPayments.pagination.total : '0'}
							label="Başarılı Ödemeler">
							<div className="loader-container low">
								<Loader loading={!successfulPayments} />
								{successfulPayments &&
									<Table
										emptyMessage="Bu sipariş için yapılmış başarılı ödeme bulunmuyor."
										className="history-list"
										pagination={successfulPayments.pagination}
										onPaginate={(page) => paginatePayments('successful', page)}
										heading={
											<React.Fragment>
												<th>Oluşturulma Tarihi</th>
												<th>Kredi Kartı</th>
												<th>Ödeme Türü</th>
												<th>Ödenen</th>
												<th>Taksit Sayısı</th>
												<th></th>
											</React.Fragment>
										}>
										{successfulPayments.payments.map((payment, nth) => (
											<TR key={nth} mobileTitle={nth + 1 + '. Ödeme'}>
												<TD>
													<p>
														{formatDate(payment.created_at, 'dd MMMM yyyy')}
													</p>
													<p className="table-subtitle">
														{formatDate(payment.created_at, 'HH:mm')}
													</p>
												</TD>
												<TD>
													<p>{payment.card_masked}</p>
													<p className="table-subtitle">
														{payment.card_name} {(payment.card_expiry_date && payment.card_expiry_date.length > 3) && <React.Fragment>- {payment.card_expiry_date}</React.Fragment>}
													</p>
													{payment.approve_code &&
														<p className="table-subtitle">
															İşlem onay kodu: {payment.approve_code}
														</p>
													}
												</TD>
												<TD>
													<p>{payment.payment_name}</p>
													{payment.installment_count !== null &&
														<p className="table-subtitle">
															{payment.installment_count !== 1 ? (payment.installment_count + ' Taksit') : 'Tek Çekim'} - %{formatNumber((1 - (payment.price / payment.real_price)) * 100)} Vade
														</p>
													}

													{payment.is_3d &&
														<span className="label success">3D</span>
													}

													{payment.provision_type === 'Auth' &&
														<span className="label warning">Provizyon</span>
													}
												</TD>
												<TD>
													<p>
														<Pricebox
															currency={payment.order.price_type.currency_symbol}
															price={payment.price} />
													</p>
													{payment.currency_exchange_total ?
														<React.Fragment>
															<p>
																<span className="label text-regular">
																	<Pricebox
																		monochrome
																		price={payment.currency_exchange_total} />
																</span>
															</p>
															<p>
																<ConversionInfo
																	exchangeType={payment.currency_exchange_rate.currency_exchange_type.id}
																	currency={getCurrencySymbol(payment.currency_exchange_rate.currency_code, false, true)}
																	price={payment.currency_exchange_rate.currency_exchange_rate} />
															</p>

															{payment.currency_exchange_refund_total > 0 &&
																<p>
																	<span className="label error">
																		İade : <Pricebox monochrome currency="₺" price={payment.currency_exchange_refund_total} />
																	</span>
																</p>
															}
														</React.Fragment>
													:
														<React.Fragment>
															{payment.real_price !== payment.price &&
																<p>
																	<span className="label warning">
																		Çekilen: <Pricebox
																			monochrome
																			price={payment.real_price}
																			currency={payment.order.price_type.currency_symbol} />
																	</span>
																</p>
															}
															{(payment.refund_total && payment.refund_total !== "0") &&
																<p>
																	<span className="label error">
																	İade: <Pricebox monochrome currency={payment.order.price_type.currency_symbol} price={payment.refund_total} />
																	</span>
																</p>
															}
														</React.Fragment>
													}
												</TD>
												<TD>
													<p className="table-subtitle">{payment.installment_count} Taksit</p>
												</TD>
												<TD>
													{((payment.status === 1 && !data.is_product_order && payment.provision_type !== 'Auth') || (payment.provision_type === 'Auth' && payment.status === 1 && payment.provision_approve_status === 1)) &&
														<Btn
															onClick={() => {
																openModal(
																	'refund-payment',
																	{
																		payment: payment,
																		onSuccess: () => {
																			getData(true);
																			getSubPayments('successful', successfulPaymentQuery);
																			getSubPayments('refund', refundQuery);
																		}
																	}
																)
															}}
															outline error small
															icon="trash">
															İade Et
														</Btn>
													}
													{(payment.status === 3 && payment.provision_type === 'Auth') && (
														<p className="text-error">
															Provizyon iptal edildi
														</p>
													)}
													{(payment.provision_approve_status === null && payment.provision_type === 'Auth') &&
														<div style={{display: 'inline-flex', alignItems: 'center', gap: 10}}>
															<Btn
																disabled={loadingPayment === 'reverse:' + payment.id}
																loading={loadingPayment === 'reverse:' + payment.id}
																onClick={async () => {
																	setLoadingPayment('reverse:' + payment.id)
																	try {
																		await commerceServices.reverseProvisionPayment({
																			payment_id: payment.id
																		})
																		setSuccessfulPayments(false);
																		orderServices.listPayments({
																			page: 1,
																			sort_by: 'created_at',
																			status_code: 1,
																			order_business_code: orderID,
																		}).then((payload) => {
																			setSuccessfulPayments(payload);
																		}).catch(() => {
																			setSuccessfulPayments(null);
																		})
																	} finally {
																		setLoadingPayment(false)
																	}
																}}
																outline error small
																icon="close">
																İptal Et
															</Btn>
															<Btn
																disabled={loadingPayment === 'approve:' + payment.id}
																loading={loadingPayment === 'approve:' + payment.id}
																onClick={async () => {
																	setLoadingPayment('approve:' + payment.id)
																	try {
																		await commerceServices.approveProvisionPayment({
																			payment_id: payment.id
																		})
																		setSuccessfulPayments(false);
																		orderServices.listPayments({
																			page: 1,
																			sort_by: 'created_at',
																			status_code: 1,
																			order_business_code: orderID,
																		}).then((payload) => {
																			setSuccessfulPayments(payload);
																		}).catch(() => {
																			setSuccessfulPayments(null);
																		})
																	} finally {
																		setLoadingPayment(false)
																	}
																}}
																outline success small
																icon="check">
																Onayla
															</Btn>
														</div>
													}
												</TD>
											</TR>
										))}
									</Table>
								}
							</div>
						</div>
					}
					{failedPayments !== null &&
						<div
							index="failed"
							badge={failedPayments ? failedPayments.pagination.total : '0'}
							label="Başarısız Ödemeler">
							<div className="loader-container low">
								<Loader loading={!failedPayments} />
								{failedPayments &&
									<Table
										emptyMessage="Bu sipariş için yapılmış başarısız ödeme bulunmuyor."
										className="history-list"
										pagination={failedPayments.pagination}
										onPaginate={(page) => paginatePayments('failed', page)}
										heading={
											<React.Fragment>
												<th>Tarih</th>
												<th>Kredi Kartı</th>
												<th>Ödeme Türü</th>
												<th>Ödenen</th>
												<th>Grup Adı</th>
												<th>Hata Mesajı</th>
											</React.Fragment>
										}>
										{failedPayments.payments.map((payment, nth) => (
											<TR key={nth} mobileTitle={nth + 1 + '. Ödeme'}>
												<TD>
													<p>
														{formatDate(payment.created_at, 'dd MMMM yyyy')}
													</p>
													<p className="table-subtitle">
														{formatDate(payment.created_at, 'HH:mm')}
													</p>
												</TD>
												<TD>
													<p>{payment.card_masked}</p>
													<p className="table-subtitle">
														{payment.card_name} {(payment.card_expiry_date && payment.card_expiry_date.length > 3) && <React.Fragment>- {payment.card_expiry_date}</React.Fragment>}
													</p>
													{payment.approve_code &&
														<p className="table-subtitle">
															İşlem onay kodu: {payment.approve_code}
														</p>
													}
												</TD>
												<TD>
													<p>{payment.payment_name}</p>
													{payment.is_3d &&
														<span className="label success">3D</span>
													}
												</TD>
												<TD>
													<p>
														<Pricebox
															warning
															currency={payment.order.price_type.currency_symbol}
															price={payment.price} />
													</p>
													{payment.currency_exchange_total > 0 ?
														<React.Fragment>
															<p>
																<span className="label text-regular">
																	<Pricebox
																		monochrome
																		price={payment.currency_exchange_total} />
																</span>
															</p>
															<p>
																<ConversionInfo
																	exchangeType={payment.currency_exchange_rate.currency_exchange_type}
																	currency={getCurrencySymbol(payment.currency_exchange_rate.currency_code, false, true)}
																	price={payment.currency_exchange_rate.currency_exchange_rate} />
															</p>

															{payment.currency_exchange_refund_total > 0 &&
																<p>
																	<span className="label error">
																		İade : <Pricebox monochrome currency={payment.order.price_type.currency_symbol} price={payment.currency_exchange_refund_total} />
																	</span>
																</p>
															}
														</React.Fragment>
													:
														<React.Fragment>
															{payment.real_price !== payment.price &&
																<p>
																	<span className="label warning">
																		Çekilen :
																		<Pricebox
																			monochrome
																			price={payment.real_price} />
																	</span>
																</p>
															}
															{payment.refund_total > 0 &&
																<p>
																	<span className="label error">
																	İade : <Pricebox monochrome price={payment.refund_total} />
																	</span>
																</p>
															}
														</React.Fragment>
													}
												</TD>
												<TD>
													{payment.order.group.name}
												</TD>
												<TD>
													{payment.error_code !== null && <span className="label error">{payment.error_code}:</span> } <span className="text-error">"{payment.error_message}"</span>
												</TD>
											</TR>
										))}
									</Table>
								}
							</div>
						</div>
					}

					{refunds !== null &&
						<div
							index="refunds"
							badge={refunds ? refunds.pagination.total : '0'}
							label="İade Edilenler">
							<div className="loader-container low">
								<Loader loading={!refunds} />
								{refunds &&
									<Table
										emptyMessage="Bu sipariş için yapılmış iade bulunmuyor."
										className="history-list"
										pagination={refunds.pagination}
										onPaginate={(page) => paginatePayments('refund', page)}
										heading={
											<React.Fragment>
												<th>İade Tarihi</th>
												<th>İade Yapan</th>
												<th>İade İşlem Bilgisi</th>
												<th>Ödeme Kodu</th>
												<th>Ödeme İşlem Bilgisi</th>
											</React.Fragment>
										}>
										{refunds.refunds.map((payment, nth) => (
											<TR key={nth} mobileTitle={nth + 1 + '. Ödeme'}>
												<TD>
													<p>
														{formatDate(payment.created_at, 'dd MMMM yyyy')}
													</p>
													<p className="table-subtitle">
														{formatDate(payment.created_at, 'HH:mm')}
													</p>
												</TD>
												<TD>
													{payment.account.name} {payment.account.surname}
												</TD>
												<TD>
													<p>
														{payment.refund_exchange_price && payment.refund_exchange_price !== "0" ?
															<span className="price-conversion">
																<span className="label error">
																	<Pricebox
																		monochrome
																		currency={payment.order.price_type.currency_symbol}
																		price={payment.real_price} />
																</span>
																<Pricebox
																	monochrome
																	price={payment.refund_exchange_price} />
															</span>
														:
															<span className="label error">
																<Pricebox
																	monochrome
																	currency={payment.order.price_type.currency_symbol}
																	price={payment.real_price} />
															</span>
														}
													</p>

													<p className="table-subtitle">
														{payment.payment.card_name} / {payment.payment.card_masked}
													</p>
													<p className="table-subtitle">
														İşlem onay kodu: {payment.approve_code}
													</p>
													<p>
														{payment.payment.payment_type.name}
													</p>
												</TD>
												<TD>
													{payment.transaction_id}
												</TD>
												<TD>
													<p>
														<Pricebox
															currency={payment.order.price_type.currency_symbol}
															price={payment.payment.price} />
													</p>
													{payment.payment.currency_exchange_total &&
														<p>
															<span className="label dark text-regular">
																<Pricebox
																	monochrome
																	price={payment.payment.currency_exchange_total} />
															</span>
														</p>
													}
													{payment.payment.real_price !== payment.payment.price &&
														<React.Fragment>
															{(payment.payment.refund_exchange_price && payment.payment.refund_exchange_price !== "0") ?
																<React.Fragment>
																	<p>
																		<span className="label warning">
																			Vade Farklı TL:
																			<Pricebox monochrome price={payment.payment.refund_exchange_price} />
																		</span>
																	</p>
																	<p>
																		<span className="label">
																			Vade Farklı Döviz Karşılığı:
																			<Pricebox monochrome currency={payment.order.price_type.currency_symbol} price={payment.payment.real_price} />
																		</span>
																	</p>
																</React.Fragment>
																:
																<p>
																	<span className="label warning">
																		Ödenen :
																		<Pricebox
																			monochrome
																			currency={payment.order.price_type.currency_symbol}
																			price={payment.payment.real_price} />
																	</span>
																</p>
															}
														</React.Fragment>
													}
													<p className="table-subtitle">
														{formatDate(payment.payment.created_at)}
													</p>
													<p className="table-subtitle">
														İşlem onay kodu: {payment.payment.approve_code}
													</p>

													<p className="table-subtitle">
														{payment.payment.payment_type.name}
													</p>

													<p className="table-subtitle">
														{payment.payment.installment_count} Taksit
													</p>

													{payment.is_3d &&
														<span className="label success">3D</span>
													}
												</TD>
											</TR>
										))}
									</Table>
								}
							</div>
						</div>
					}
				</Tabs>
			</div>
		</main>
	)
}

export default connect(mapStateToProps)(OrderDetail);
